// app/javascript/controllers/ui/monkey_juggler_controller.js

import { Controller } from "@hotwired/stimulus"

/**
 * Monkey Juggler Controller
 * Вариант 2: Вращающийся контейнер + контр-вращение элементов
 * 
 * Мировой стандарт: Progressive Enhancement, Performance-first
 * 
 * Responsibilities:
 * - Динамическое создание элементов интеграций
 * - Позиционирование по кругу через JavaScript
 * - Вращение контейнера через CSS (GPU-accelerated)
 * - Контр-вращение элементов для сохранения вертикальности
 * - Detect reduced motion preference
 * - Lazy-start animation (Intersection Observer)
 * - Pause/play control
 */
export default class extends Controller {
  static targets = [
    "stage", 
    "monkey", 
    "itemsContainer",
    "itemsData",
    "controls",
    "toggleButton"
  ]
  
  static values = {
    autoStart: { type: Boolean, default: true },
    reducedMotion: { type: Boolean, default: false },
    radius: { type: Number, default: 220 },
    speed: { type: Number, default: 30 }
  }

  connect() {
    console.log('[MonkeyJuggler] Connected')
    
    // Detect reduced motion preference
    this.detectReducedMotion()
    
    // Адаптивный радиус (должен быть рассчитан до создания элементов)
    this.calculateAdaptiveRadius()
    
    // Загружаем данные интеграций и создаем элементы
    // Используем requestAnimationFrame для гарантии что DOM готов
    requestAnimationFrame(() => {
      this.loadIntegrationsData()
    })
    
    // Lazy-start with Intersection Observer
    if (this.autoStartValue) {
      this.setupIntersectionObserver()
    }
    
    // Setup controls
    this.isPaused = false
    
    // Обработка изменения размера окна
    this.handleResize = this.handleResize.bind(this)
    window.addEventListener('resize', this.handleResize)
  }
  
  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
    window.removeEventListener('resize', this.handleResize)
  }
  
  /**
   * Обработка изменения размера окна (адаптивность)
   */
  handleResize() {
    // Пересчитываем радиус при изменении размера
    const oldRadius = this.radiusValue
    this.calculateAdaptiveRadius()
    
    // Если радиус изменился, пересоздаем элементы
    if (oldRadius !== this.radiusValue && this.hasItemsDataTarget) {
      this.loadIntegrationsData()
    }
  }

  /**
   * Определяет предпочтения пользователя по анимации
   * Мировой стандарт: accessibility-first
   */
  detectReducedMotion() {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    
    this.reducedMotionValue = prefersReducedMotion
    
    if (prefersReducedMotion) {
      this.stageTarget.dataset.reducedMotion = 'true'
      console.log('[MonkeyJuggler] Reduced motion detected, simplifying animations')
    }
    
    // Listen for changes
    window.matchMedia('(prefers-reduced-motion: reduce)')
      .addEventListener('change', (e) => {
        this.reducedMotionValue = e.matches
        this.stageTarget.dataset.reducedMotion = e.matches
      })
  }

  /**
   * Рассчитывает адаптивный радиус в зависимости от размера экрана
   */
  calculateAdaptiveRadius() {
    const width = window.innerWidth
    
    if (width >= 1024) {
      // Desktop
      this.radiusValue = 220
      this.speedValue = 30
    } else if (width >= 768) {
      // Tablet
      this.radiusValue = 160
      this.speedValue = 35
    } else {
      // Mobile
      this.radiusValue = 110
      this.speedValue = 40
    }
    
    // Применяем скорость к CSS переменной
    if (this.hasItemsContainerTarget) {
      this.itemsContainerTarget.style.setProperty('--rotation-speed', `${this.speedValue}s`)
    }
  }

  /**
   * Загружает данные интеграций из JSON script tag
   */
  loadIntegrationsData() {
    try {
      const dataElement = this.itemsDataTarget
      if (!dataElement) {
        console.warn('[MonkeyJuggler] No integrations data found')
        return
      }
      
      const integrations = JSON.parse(dataElement.textContent)
      
      if (!integrations || integrations.length === 0) {
        console.warn('[MonkeyJuggler] No integrations to display')
        return
      }
      
      console.log(`[MonkeyJuggler] Loading ${integrations.length} integrations`)
      this.createIntegrationElements(integrations)
      
    } catch (error) {
      console.error('[MonkeyJuggler] Failed to parse integrations data:', error)
    }
  }

  /**
   * Создает элементы интеграций и позиционирует их по кругу
   */
  createIntegrationElements(integrations) {
    if (!this.hasItemsContainerTarget) {
      console.error('[MonkeyJuggler] Items container not found')
      return
    }
    
    const container = this.itemsContainerTarget
    
    // Используем offsetWidth/offsetHeight вместо getBoundingClientRect
    // Это работает даже если элемент еще не полностью отрендерен
    const stageWidth = this.stageTarget.offsetWidth || 900
    const stageHeight = this.stageTarget.offsetHeight || 600
    
    // Центр контейнера (50% от размеров stage)
    const centerX = stageWidth / 2
    const centerY = stageHeight / 2
    
    // Радиус орбиты
    const radius = this.radiusValue
    
    // Очищаем контейнер (на случай повторного вызова)
    container.innerHTML = ''
    
    // Создаем элементы для каждой интеграции
    integrations.forEach((integration, index) => {
      const element = this.createIntegrationElement(integration, index, centerX, centerY, radius, integrations.length)
      container.appendChild(element)
    })
    
    console.log(`[MonkeyJuggler] Created ${integrations.length} integration elements at center (${centerX}, ${centerY}) with radius ${radius}`)
  }

  /**
   * Создает HTML элемент для одной интеграции
   */
  createIntegrationElement(integration, index, centerX, centerY, radius, totalItems) {
    // Расчет угла для равномерного распределения
    const angleStep = 360 / totalItems
    const angle = angleStep * index
    
    // Конвертация в радианы (-90 чтобы первый элемент был сверху)
    const radians = (angle - 90) * (Math.PI / 180)
    
    // Расчет координат на окружности
    const x = centerX + radius * Math.cos(radians)
    const y = centerY + radius * Math.sin(radians)
    
    // Создаем wrapper элемент
    const wrapper = document.createElement('div')
    wrapper.className = `juggle-item ${integration.status}`
    wrapper.dataset.integrationId = integration.id
    
    // Применяем позицию через inline style
    wrapper.style.left = `${x}px`
    wrapper.style.top = `${y}px`
    wrapper.style.setProperty('--item-color', integration.color || '#9CA3AF')
    
    // Иконка
    const iconDiv = document.createElement('div')
    iconDiv.className = 'juggle-item__icon'
    
    // SVG иконка (приходит уже отрендеренная из Rails)
    // Нужно будет передать SVG HTML из Rails
    iconDiv.innerHTML = integration.svg_html || this.createFallbackIcon(integration.icon)
    
    // Label (показывается при hover)
    const labelDiv = document.createElement('div')
    labelDiv.className = 'juggle-item__label'
    const statusText = integration.status === 'available' ? 'Доступно' : 'Скоро'
    labelDiv.innerHTML = `
      ${integration.name}
      <span class="label-status">${statusText}</span>
    `
    
    // Собираем все вместе
    wrapper.appendChild(iconDiv)
    wrapper.appendChild(labelDiv)
    
    return wrapper
  }

  /**
   * Создает fallback иконку если SVG не передан
   */
  createFallbackIcon(iconName) {
    return `<div class="integration-fallback-icon">${iconName?.charAt(0).toUpperCase() || '?'}</div>`
  }

  /**
   * Запускает анимацию только когда секция видна
   * Мировой стандарт: performance optimization (как у Stripe)
   */
  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3 // 30% видимости для старта
    }
    
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.startAnimation()
          // Отключаем observer после первого запуска
          this.observer.unobserve(entry.target)
        }
      })
    }, options)
    
    this.observer.observe(this.stageTarget)
  }

  /**
   * Запускает анимацию (добавляет CSS класс)
   */
  startAnimation() {
    if (this.reducedMotionValue) return
    
    this.stageTarget.classList.add('animating')
    console.log('[MonkeyJuggler] Animation started')
  }

  /**
   * Переключает паузу/воспроизведение
   */
  toggleAnimation() {
    this.isPaused = !this.isPaused
    
    if (this.isPaused) {
      this.pauseAnimation()
    } else {
      this.resumeAnimation()
    }
  }

  /**
   * Ставит анимацию на паузу
   */
  pauseAnimation() {
    this.stageTarget.classList.add('paused')
    
    // Меняем иконку кнопки
    const pauseIcon = this.toggleButtonTarget.querySelector('.icon-pause')
    const playIcon = this.toggleButtonTarget.querySelector('.icon-play')
    
    if (pauseIcon && playIcon) {
      pauseIcon.classList.add('hidden')
      playIcon.classList.remove('hidden')
    }
    
    this.toggleButtonTarget.setAttribute(
      'aria-label', 
      'Возобновить анимацию'
    )
    
    console.log('[MonkeyJuggler] Animation paused')
  }

  /**
   * Возобновляет анимацию
   */
  resumeAnimation() {
    this.stageTarget.classList.remove('paused')
    
    // Меняем иконку кнопки
    const pauseIcon = this.toggleButtonTarget.querySelector('.icon-pause')
    const playIcon = this.toggleButtonTarget.querySelector('.icon-play')
    
    if (pauseIcon && playIcon) {
      pauseIcon.classList.remove('hidden')
      playIcon.classList.add('hidden')
    }
    
    this.toggleButtonTarget.setAttribute(
      'aria-label', 
      'Приостановить анимацию'
    )
    
    console.log('[MonkeyJuggler] Animation resumed')
  }
}
